import type {
  ContentQueueInsert,
  ContentQueueItem,
  ContentQueueStatus,
  ContentQueueUpdate,
} from '@/types/customer-ops';

const LAZY_RIVER_LOC_ID = 'loc-040';

const DEV_SEED_CONTENT: ContentQueueItem[] = [
  {
    id: 'dev-content-lr-basket-lift',
    title: 'Data highlight: Family basket lift at Lazy River',
    content_type: 'data_highlight',
    channel: 'linkedin',
    status: 'draft',
    draft_text: [
      '[customer-ops-pr13-seed]',
      '[proof_lrp_basket_lift] Family basket lift',
      '',
      'Lazy River saw measurable basket lift after floor optimization — verify opt-in before naming publicly.',
      '',
      'Approval: Marketing + TPB leadership before publish.',
    ].join('\n'),
    approver: null,
    scheduled_for: null,
    published_url: null,
    killed_reason: null,
    proof_metric_ids: ['proof_lrp_basket_lift'],
    location_id: LAZY_RIVER_LOC_ID,
    jurisdiction_tag: 'ALL-US',
    trigger_source: 'proof_library',
    notes: '[customer-ops-pr13-seed] Sample content queue row for local QA.',
    created_at: '2026-06-01T10:00:00Z',
    updated_at: '2026-06-01T10:00:00Z',
  },
];

let devContentQueueRows: ContentQueueItem[] = [...DEV_SEED_CONTENT];

function trimNullable(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function sortRows(rows: ContentQueueItem[]): ContentQueueItem[] {
  return [...rows].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export function mergeDevCustomerOpsContentQueue(rows: ContentQueueItem[]): ContentQueueItem[] {
  const byId = new Map<string, ContentQueueItem>();
  for (const row of [...devContentQueueRows, ...rows]) {
    byId.set(row.id, row);
  }
  return sortRows([...byId.values()]);
}

export function findDevCustomerOpsContentQueueById(id: string): ContentQueueItem | null {
  return devContentQueueRows.find((row) => row.id === id) ?? null;
}

export function devCustomerOpsContentQueueCreate(data: ContentQueueInsert): ContentQueueItem {
  const now = new Date().toISOString();
  const row: ContentQueueItem = {
    id: `dev-content-${Date.now()}`,
    title: data.title.trim(),
    content_type: data.content_type,
    channel: data.channel,
    status: data.status ?? 'draft',
    draft_text: data.draft_text ?? '',
    approver: trimNullable(data.approver),
    scheduled_for: data.scheduled_for ?? null,
    published_url: trimNullable(data.published_url),
    killed_reason: trimNullable(data.killed_reason),
    proof_metric_ids: data.proof_metric_ids ?? [],
    location_id: data.location_id ?? null,
    jurisdiction_tag: data.jurisdiction_tag?.trim() || 'ALL-US',
    trigger_source: data.trigger_source ?? 'manual',
    notes: trimNullable(data.notes),
    created_at: now,
    updated_at: now,
  };
  devContentQueueRows = [row, ...devContentQueueRows];
  return row;
}

export function devCustomerOpsContentQueueUpdate(id: string, data: ContentQueueUpdate): ContentQueueItem {
  const index = devContentQueueRows.findIndex((row) => row.id === id);
  if (index === -1) {
    throw new Error('Content queue row not found in dev store');
  }
  const existing = devContentQueueRows[index]!;
  const updated: ContentQueueItem = {
    ...existing,
    title: data.title !== undefined ? data.title.trim() : existing.title,
    content_type: data.content_type ?? existing.content_type,
    channel: data.channel ?? existing.channel,
    status: data.status ?? existing.status,
    draft_text: data.draft_text ?? existing.draft_text,
    approver: data.approver !== undefined ? trimNullable(data.approver) : existing.approver,
    scheduled_for: data.scheduled_for !== undefined ? data.scheduled_for : existing.scheduled_for,
    published_url: data.published_url !== undefined ? trimNullable(data.published_url) : existing.published_url,
    killed_reason: data.killed_reason !== undefined ? trimNullable(data.killed_reason) : existing.killed_reason,
    proof_metric_ids: data.proof_metric_ids ?? existing.proof_metric_ids,
    location_id: data.location_id !== undefined ? data.location_id : existing.location_id,
    jurisdiction_tag: data.jurisdiction_tag !== undefined ? data.jurisdiction_tag.trim() || 'ALL-US' : existing.jurisdiction_tag,
    trigger_source: data.trigger_source ?? existing.trigger_source,
    notes: data.notes !== undefined ? trimNullable(data.notes) : existing.notes,
    updated_at: new Date().toISOString(),
  };
  devContentQueueRows[index] = updated;
  return updated;
}

async function tryRemoteOrDev<T>(remote: () => Promise<T>, fallback: () => T): Promise<T> {
  try {
    return await remote();
  } catch {
    return fallback();
  }
}

export function createDevContentQueueServiceWrapper(
  service: typeof import('./supabase/content-queue').contentQueueService
) {
  return {
    ...service,
    async list(...args: Parameters<typeof service.list>) {
      const remote = await service.list(...args);
      return mergeDevCustomerOpsContentQueue(remote);
    },
    async getById(id: string) {
      const remote = await service.getById(id);
      if (remote) return remote;
      return findDevCustomerOpsContentQueueById(id);
    },
    async create(data: ContentQueueInsert) {
      return tryRemoteOrDev(
        () => service.create(data),
        () => devCustomerOpsContentQueueCreate(data)
      );
    },
    async update(id: string, data: ContentQueueUpdate) {
      if (findDevCustomerOpsContentQueueById(id)) {
        return devCustomerOpsContentQueueUpdate(id, data);
      }
      return tryRemoteOrDev(
        () => service.update(id, data),
        () => devCustomerOpsContentQueueUpdate(id, data)
      );
    },
    async setStatus(
      id: string,
      status: ContentQueueStatus,
      fields?: Parameters<typeof service.setStatus>[2]
    ) {
      if (findDevCustomerOpsContentQueueById(id)) {
        return devCustomerOpsContentQueueUpdate(id, { status, ...fields });
      }
      return tryRemoteOrDev(
        () => service.setStatus(id, status, fields),
        () => devCustomerOpsContentQueueUpdate(id, { status, ...fields })
      );
    },
    async countByStatus(statuses: ContentQueueStatus[]) {
      const rows = await this.list({ status: statuses });
      return rows.length;
    },
  };
}
